/**
 * Brand directory: search and A-Z filtering for the brand grid.
 *
 * The first page of collections is rendered by sections/easyr-brand-directory.liquid
 * (paginate caps at 250); the remaining pages are fetched as JSON from
 * templates/page.brands-json.liquid.
 *
 * Search and the A-Z are a single filter: typing clears the letter and choosing a letter
 * clears the search, so the two can't combine into an unexplained empty result.
 */
if (!customElements.get('easyr-brand-directory')) {
  const COMBINING_MARKS = /[̀-ͯ]/g;

  class EasyrBrandDirectory extends HTMLElement {
    constructor() {
      super();
      this.grid = this.querySelector('[data-bd-grid]');
      this.searchInput = this.querySelector('[data-bd-search]');
      this.clearBtn = this.querySelector('[data-bd-clear]');
      this.azGroup = this.querySelector('[data-bd-az]');
      this.countEl = this.querySelector('[data-bd-count]');
      this.emptyEl = this.querySelector('[data-bd-empty]');
      this.emptyMsg = this.querySelector('[data-bd-empty-msg]');
      this.resetBtn = this.querySelector('[data-bd-reset]');

      this.letter = 'all';
      this.term = '';
      this.started = false;
    }

    connectedCallback() {
      // Guard against a second connect (element moved in the DOM) re-fetching everything.
      if (this.started) return;
      this.started = true;

      this.cards = Array.from(this.grid.querySelectorAll('.easyr-brand-card'));
      this.cards.forEach(EasyrBrandDirectory.indexCard);

      /* The first page covers only the start of the alphabet, so letter availability
         waits until every page has loaded. */
      this.complete = !this.querySelector('[data-bd-pages]');

      this.bindEvents();
      if (this.complete) this.syncLetterAvailability();
      this.render({ animate: false });
      this.loadRemainingPages();
    }

    bindEvents() {
      if (this.searchInput) {
        // Debounced: each render runs a measure-and-animate pass.
        let debounce;
        this.searchInput.addEventListener('input', () => {
          clearTimeout(debounce);
          debounce = setTimeout(() => {
            this.term = this.searchInput.value.trim();
            this.letter = 'all';
            this.render();
          }, 120);
        });
      }

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          this.searchInput.value = '';
          this.term = '';
          this.searchInput.focus();
          this.render();
        });
      }

      if (this.azGroup) {
        this.azGroup.addEventListener('click', (evt) => {
          const btn = evt.target.closest('[data-bd-letter]');
          if (!btn || btn.disabled) return;
          this.letter = btn.dataset.bdLetter;
          this.term = '';
          if (this.searchInput) this.searchInput.value = '';
          this.render();
        });
      }

      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', () => {
          this.letter = 'all';
          this.term = '';
          if (this.searchInput) this.searchInput.value = '';
          this.render();
          if (this.searchInput) this.searchInput.focus();
        });
      }
    }

    /**
     * Case- and accent-folds a string for matching.
     * NFD lengthens accented text ("Öhlins" is 7 code points), so the returned map gives
     * each folded character's index in the original, for highlighting the right letters.
     * @param {string} str
     * @returns {{folded: string, map: number[]}}
     */
    static fold(str) {
      let folded = '';
      const map = [];
      for (let i = 0; i < str.length; i += 1) {
        const chunk = str[i].toLowerCase().normalize('NFD').replace(COMBINING_MARKS, '');
        for (let j = 0; j < chunk.length; j += 1) {
          folded += chunk[j];
          map.push(i);
        }
      }
      return { folded, map };
    }

    /** Caches a card's folded name, so matching doesn't re-fold on every keystroke. */
    static indexCard(card) {
      const { folded, map } = EasyrBrandDirectory.fold(card.dataset.bdName || '');
      card._bdFolded = folded;
      card._bdMap = map;
    }

    /** Disables letters that have no brands. */
    syncLetterAvailability() {
      if (!this.azGroup) return;
      const present = new Set(this.cards.map((c) => c.dataset.bdLetter));
      this.azGroup.querySelectorAll('[data-bd-letter]').forEach((btn) => {
        const val = btn.dataset.bdLetter;
        if (val === 'all') return;
        btn.disabled = !present.has(val);
      });
    }

    matches(card, foldedTerm) {
      if (foldedTerm) return card._bdFolded.includes(foldedTerm);
      return this.letter === 'all' || card.dataset.bdLetter === this.letter;
    }

    /** Wraps the matched part of the card's name in <mark>, or restores the plain name. */
    highlight(card, foldedTerm) {
      const nameEl = card.querySelector('.easyr-brand-card__name');
      if (!nameEl) return;
      const name = card.dataset.bdName;

      const idx = foldedTerm ? card._bdFolded.indexOf(foldedTerm) : -1;

      if (idx === -1) {
        // Skip the DOM write when the text is already plain; this runs per card.
        if (card._bdMarked) {
          nameEl.textContent = name;
          card._bdMarked = false;
        }
        return;
      }

      // Translate folded indices back to the original string before slicing.
      const start = card._bdMap[idx];
      const endFolded = idx + foldedTerm.length - 1;
      const end = card._bdMap[endFolded] + 1;

      nameEl.textContent = '';
      nameEl.append(
        document.createTextNode(name.slice(0, start)),
        Object.assign(document.createElement('mark'), {
          textContent: name.slice(start, end)
        }),
        document.createTextNode(name.slice(end))
      );
      card._bdMarked = true;
    }

    /**
     * FLIP, step one: records every visible card's position before a filter is applied.
     * Off-screen cards are included, since they may land on screen after filtering.
     * @returns {Map<Element, DOMRect>}
     */
    capturePositions() {
      const map = new Map();
      if (this.reducedMotion()) return map;
      this.cards.forEach((card) => {
        if (card.hidden) return;
        map.set(card, card.getBoundingClientRect());
      });
      return map;
    }

    /**
     * FLIP, step two: animates each card from its recorded position to its new one.
     * Uses the Web Animations API rather than inline styles and a CSS transition, so a
     * card's resting state never depends on an animation finishing; if frames stop (e.g.
     * a background tab), cards stay visible instead of stuck at opacity 0.
     * @param {Map<Element, DOMRect>} first - Positions from capturePositions().
     */
    playPositions(first) {
      if (this.reducedMotion() || typeof Element.prototype.animate !== 'function') return;

      const MARGIN = 400;
      const EASE = 'cubic-bezier(0.2, 0.7, 0.3, 1)';

      /* Long moves are shortened to MAX_TRAVEL. The vector is scaled as a whole, not
         clamped per axis, so each card keeps its real direction. */
      const MAX_TRAVEL = 340;
      const shorten = (dx, dy) => {
        const dist = Math.hypot(dx, dy);
        if (dist <= MAX_TRAVEL) return [dx, dy];
        const k = MAX_TRAVEL / dist;
        return [Math.round(dx * k), Math.round(dy * k)];
      };

      const viewportHeight = window.innerHeight;
      let staggered = 0;

      this.cards.forEach((card) => {
        if (card.hidden) return;
        const r = card.getBoundingClientRect();
        // Only animate cards near the viewport.
        if (r.bottom < -MARGIN || r.top > viewportHeight + MARGIN) return;

        // Cancel any animation still running from a previous filter.
        card.getAnimations().forEach((a) => a.cancel());

        const prev = first.get(card);

        if (prev) {
          const dx = prev.left - r.left;
          const dy = prev.top - r.top;
          if (!dx && !dy) return;

          const [fx, fy] = shorten(dx, dy);
          const from = `translate(${fx}px, ${fy}px)`;

          if (Math.hypot(dx, dy) > MAX_TRAVEL) {
            // Shortened move: fade and scale in so the jump to the start point isn't visible.
            const delay = Math.min(staggered * 22, 260);
            staggered += 1;
            card.animate(
              [
                { opacity: 0, transform: `${from} scale(0.94)` },
                { opacity: 1, transform: 'none' }
              ],
              { duration: 420, delay, easing: EASE, fill: 'backwards' }
            );
          } else {
            card.animate([{ transform: from }, { transform: 'none' }], {
              duration: 420,
              easing: EASE
            });
          }
        } else {
          // No previous position: the card arrived with a later JSON page.
          const delay = Math.min(staggered * 16, 200);
          staggered += 1;
          card.animate(
            [
              { opacity: 0, transform: 'translateY(16px) scale(0.98)' },
              { opacity: 1, transform: 'none' }
            ],
            { duration: 300, delay, easing: EASE, fill: 'backwards' }
          );
        }
      });
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    render({ animate = true } = {}) {
      const foldedTerm = this.term ? EasyrBrandDirectory.fold(this.term).folded : '';
      const first = animate ? this.capturePositions() : new Map();
      let shown = 0;

      this.cards.forEach((card) => {
        const hit = this.matches(card, foldedTerm);
        card.hidden = !hit;
        if (hit) {
          shown += 1;
          this.highlight(card, foldedTerm);
        }
      });

      const total = this.cards.length;

      if (this.countEl) {
        if (!this.complete) {
          // No total until every page has loaded.
          this.countEl.textContent = this.dataset.loadingLabel || 'Loading brands…';
        } else {
          this.countEl.textContent = shown === total ? `${total} brands` : `${shown} of ${total} brands`;
        }
      }

      if (this.emptyEl) {
        this.emptyEl.hidden = shown !== 0;
        if (shown === 0 && this.emptyMsg) {
          if (this.term) {
            this.emptyMsg.textContent = `Nothing matching “${this.term}”.`;
          } else if (this.letter !== 'all') {
            this.emptyMsg.textContent = `No brands start with “${this.letter}”.`;
          } else {
            this.emptyMsg.textContent = '';
          }
        }
      }

      this.grid.hidden = shown === 0;

      if (this.clearBtn) this.clearBtn.hidden = !this.term;

      if (this.azGroup) {
        this.azGroup.querySelectorAll('[data-bd-letter]').forEach((btn) => {
          btn.setAttribute('aria-pressed', String(!this.term && btn.dataset.bdLetter === this.letter));
        });
      }

      if (animate) this.playPositions(first);
    }

    async loadRemainingPages() {
      const pagesEl = this.querySelector('[data-bd-pages]');
      const feed = this.dataset.feedUrl;

      let pages = 0;
      if (pagesEl) {
        try {
          pages = JSON.parse(pagesEl.textContent);
        } catch (err) {
          console.warn('[easyr-brand-directory] could not read page count', err);
        }
      }

      // Every exit path must call finishLoading(), or the count and A-Z never settle.
      if (!pages || pages < 2 || !feed) {
        this.finishLoading();
        return;
      }

      /* No `Accept: application/json` header: with it, Shopify ignores `?view=` and returns
         its own page resource, which parses as JSON but has no `brands`. */
      const requests = [];
      for (let page = 2; page <= pages; page += 1) {
        requests.push(
          fetch(`${feed}&page=${page}`)
            .then((res) => (res.ok ? res.text() : null))
            .then((text) => {
              if (!text) return null;
              try {
                return JSON.parse(text);
              } catch (err) {
                console.warn('[easyr-brand-directory] page did not return JSON', err);
                return null;
              }
            })
            .catch(() => null)
        );
      }

      const results = await Promise.all(requests);
      const fragment = document.createDocumentFragment();
      const showName = !!this.grid.querySelector('.easyr-brand-card__name');

      results.filter(Boolean).forEach((data) => {
        if (!Array.isArray(data.brands)) {
          console.warn('[easyr-brand-directory] response had no brands array', data);
          return;
        }
        data.brands.forEach((brand) => {
          fragment.appendChild(this.buildCard(brand, showName));
        });
      });

      this.grid.appendChild(fragment);
      this.cards = Array.from(this.grid.querySelectorAll('.easyr-brand-card'));
      this.cards.forEach(EasyrBrandDirectory.indexCard);
      this.sortCards();
      this.finishLoading();
    }

    finishLoading() {
      this.complete = true;
      this.syncLetterAvailability();
      this.render({ animate: false });
    }

    /** Sorts cards by name once all pages are in, moving them in one grid mutation. */
    sortCards() {
      const sorted = this.cards.slice().sort((a, b) =>
        a.dataset.bdName.localeCompare(b.dataset.bdName, undefined, {
          sensitivity: 'base'
        })
      );
      const fragment = document.createDocumentFragment();
      sorted.forEach((card) => fragment.appendChild(card));
      this.grid.appendChild(fragment);
      this.cards = sorted;
    }

    /**
     * Builds a brand card matching the server-rendered markup.
     * @param {Object} brand - Entry from the brands JSON feed.
     * @param {boolean} showName - Whether to include the name under the logo.
     * @returns {HTMLAnchorElement}
     */
    buildCard(brand, showName) {
      const card = document.createElement('a');
      card.className = 'easyr-brand-card';
      card.href = brand.url;
      card.dataset.bdName = brand.title;
      card.dataset.bdLetter = brand.letter;

      const media = document.createElement('span');
      media.className = 'easyr-brand-card__media';

      if (brand.image) {
        const img = document.createElement('img');
        img.src = brand.image;
        if (brand.image_2x) img.srcset = `${brand.image} 400w, ${brand.image_2x} 600w`;
        img.sizes = '(min-width: 769px) 20vw, 45vw';
        if (brand.width) img.width = brand.width;
        if (brand.height) img.height = brand.height;
        img.alt = brand.title;
        img.loading = 'lazy';
        media.appendChild(img);
      } else {
        const fallback = document.createElement('span');
        fallback.className = 'easyr-brand-card__fallback';
        fallback.textContent = brand.title;
        media.appendChild(fallback);
      }

      card.appendChild(media);

      if (showName) {
        const name = document.createElement('span');
        name.className = 'easyr-brand-card__name';
        name.textContent = brand.title;
        card.appendChild(name);
      }

      return card;
    }
  }

  customElements.define('easyr-brand-directory', EasyrBrandDirectory);
}
